import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/tailwind.utils'
import { ChevronUp, Mail, Phone } from 'lucide-react'

export type ContactPersonTypes = {
  name: string
  role: string
  email: string
  phone: string
  avatarUrl?: string
}

type ContactPersonProps = {
  contacts: [ContactPersonTypes] | [ContactPersonTypes, ContactPersonTypes]
}

const ContactCard = ({ name, role, email, phone, avatarUrl }: ContactPersonTypes) => {
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
  const formattedPhone = phone?.startsWith('64') ? `+${phone}` : phone

  return (
    <div className="flex gap-3">
      <Avatar className="size-9 shrink-0">
        {avatarUrl && <AvatarImage src={avatarUrl} alt={name} />}
        <AvatarFallback className="bg-rexBlue-100 text-rexBlue-800 text-xs font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex min-w-0 flex-col gap-1">
        <div>
          <p className="text-sm font-semibold text-rexBlue-800 leading-tight">{name}</p>
          {role && <p className="text-xs text-muted-foreground leading-tight">{role}</p>}
        </div>

        {email && (
          <a
            href={`mailto:${email}`}
            className="flex items-center gap-1.5 text-xs text-primary hover:underline truncate max-w-full"
          >
            <Mail className="size-3 shrink-0" />
            <span className="truncate">{email}</span>
          </a>
        )}

        {phone && (
          <a
            href={`tel:${formattedPhone?.replace(/\s/g, '')}`}
            className="flex items-center gap-1.5 text-xs text-foreground hover:underline"
          >
            <Phone className="size-3 shrink-0" />
            {formattedPhone}
          </a>
        )}
      </div>
    </div>
  )
}

const AvatarStack = ({ contacts }: { contacts: ContactPersonProps['contacts'] }) => (
  <div className="flex items-center -space-x-1.5">
    {contacts.map((contact, i) => {
      const initials = contact.name
        .split(' ')
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
      return (
        <Avatar key={i} className="size-6 ring-2 ring-background text-[10px]">
          {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} alt={contact.name} />}
          <AvatarFallback className="bg-rexBlue-100 text-rexBlue-800 text-[10px] font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>
      )
    })}
  </div>
)

export const ContactPerson = ({ contacts }: ContactPersonProps) => {
  return (
    <div className="fixed bottom-5 right-5 z-50">
      <Popover>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'group flex items-center gap-2 rounded-full',
              'bg-background border border-border shadow-md',
              'pl-2 pr-3.5 py-1.5',
              'text-sm font-medium text-foreground',
              'hover:bg-muted hover:border-border/80',
              'transition-all duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            )}
          >
            <AvatarStack contacts={contacts} />
            <span className="text-sm">Contacts</span>
            <ChevronUp
              className={cn(
                'size-3.5 text-muted-foreground transition-transform duration-200',
                'group-data-[state=open]:rotate-180',
              )}
            />
          </button>
        </PopoverTrigger>

        <PopoverContent side="top" align="end" sideOffset={10} className="w-auto p-3 bg-background">
          <div
            className={cn(
              'grid gap-3',
              contacts.length > 1 && 'sm:grid-cols-2 sm:divide-x sm:gap-5',
            )}
          >
            {contacts.map((contact, i) => (
              <div key={i} className={cn(i > 0 && 'sm:pl-4')}>
                <ContactCard {...contact} />
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default ContactPerson
